import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { KNM_JUDGMENTS } from 'CorePackage/MainModule/routes/routes';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsNeedActSaveOrSign,
  getKnmWatchOnlyByTestUser,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErknmFormJudgment } from '../MainModule/interfaces/IKnmErknmFormJudgment';
import { getJudgmentBlockId, JUDGMENTS_BLOCK_ID } from '../MainModule/constants/knmErknmFieldsElementsIds';
import { ErknmJudgmentItem } from './ErknmJudgmentItem';
import { catalogGet } from '../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from '../../CorePackage/MainModule/types/RootActions';

interface IErknmJudgmentProps {
  isWatchOnlyByStatus: boolean;
  isWatchOnlyByRole: boolean;
  isDecision?: boolean;
}

interface IErknmJudgmentStoreProps {
  formRef: IKnmFormRef | undefined;
  isWatchOnlyByTestUser: boolean;
  isNeedActSaveOrSign?: boolean;
}

export const ErknmJudgmentWithoutStore = ({
  isWatchOnlyByStatus,
  isWatchOnlyByRole,
  formRef,
  isDecision,
  isWatchOnlyByTestUser,
  isNeedActSaveOrSign = false,
}: IErknmJudgmentProps & IErknmJudgmentStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isWatchOnly = isWatchOnlyByStatus || isWatchOnlyByRole || isWatchOnlyByTestUser || isNeedActSaveOrSign;

  /*
    Подгружаем данные словарей только тогда, когда в разделе могут быть данные, согласно статусной модели
   */
  useEffect(() => {
    if (!isWatchOnlyByStatus) {
      dispatch(catalogGet(CommonCatalogsNamesEnum.DISTRICTS));
      dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_COURTS));
    }

    /**
     * Вынесено из проверки, т.к. в некоторых паспортах (старых) в статусе "В процессе заполнения" не отображается значение поля Уровень суда
     */
    dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_COURT_LEVELS));
  }, [isWatchOnly, dispatch]);

  const addJudgment = useCallback(() => {
    if (!isWatchOnly && push) {
      const newJudgment: IKnmErknmFormJudgment = {
        guid: undefined,
        dateJudgment: undefined,
        numberJudgment: undefined,
        attachment: undefined,
        kindJudgment: undefined,
        rfRegionCode: undefined,
        court: undefined,
        courtLevelCode: undefined,
        judgmentAction: undefined,
        judgmentSubjectData: undefined,
        canceledJudgments: [],
        createNewJudgment: false,
        listId: uuidv4(),
        isNew: true,
      };

      push('judgments', newJudgment);
    }
  }, [push, isWatchOnly]);

  const makeRemoveJudgmentHandler = (index: number) => () => !isWatchOnly && remove && remove('judgments', index);

  return (
    <Section
      title={`Сведения о судебных решениях, принятых по результатам ${!isDecision ? 'КНМ' : 'решения'}`}
      type="list"
      id={KNM_JUDGMENTS}
      titleId={JUDGMENTS_BLOCK_ID}
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={isWatchOnly}
          onClick={addJudgment}
        />
      }
    >
      <FieldArray name="judgments">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgment, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErknmFormJudgment = fields.value?.[index];
            const id = getJudgmentBlockId(fieldValue.listId);

            return (
              <ErknmJudgmentItem
                value={fieldValue}
                key={id}
                id={id}
                name={name}
                watchOnly={isWatchOnly}
                onRemove={makeRemoveJudgmentHandler(index)}
              />
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IErknmJudgmentStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
});

export const ErknmJudgment = connect(mapStateToProps)(ErknmJudgmentWithoutStore);
